/**
 * Port-Based Node Selection & Validation Rules
 *
 * Centralized rules for determining:
 * 1. Which node categories are selectable when clicking a port
 * 2. Which tabs should be visible in the node selection modal
 * 3. Validation rules to enforce these constraints
 *
 * Special Cases:
 * - Tool node outputs: Automatically restricted to Tool category only (chaining tools)
 * - Trigger node outputs: Cannot connect to other triggers
 * - AI Agent extends/team-leader ports: Only allow AI Agent nodes
 *
 * These rules are reusable across:
 * - NodeSearchModal (filtering displayed nodes)
 * - Node validation logic (preventing invalid connections)
 * - Edge creation handlers
 */

export type PortType =
  | 'output'                 // Standard output port (from any CustomNode / SubFlowNode)
  | 'trigger'                // Trigger node output (manual-trigger, webhook-trigger, schedule-trigger)
  | 'llm'                    // AI Agent bottom → LLM node (openai-llm)
  | 'memory'                 // AI Agent bottom → memory nodes
  | 'tool'                   // AI Agent bottom → tool nodes (multi)
  | 'agentInherit'           // AI Agent right "inherit" source → another agent's inheritfrom
  | 'agentParticipation'     // AI Agent top "team-leader" source → another agent's team-member (multi)
  | 'agentInheritTarget'     // AI Agent bottom "base-node" target — receives from agentInherit
  | 'agentParticipantTarget' // AI Agent bottom "team-member" target — receives from agentParticipation (multi)
  | 'input'                  // Standard input port (left handle on most nodes)
  | 'knowledgeBase'          // Knowledge base port
  | 'conditionTrue'          // Diamond node TRUE branch output
  | 'conditionFalse'         // Diamond node FALSE branch output
  | 'conditionCase'          // Diamond node switch-case branch output
  | 'component';             // my-components source → any node's component input (multi)

export type NodeCategory =
  | 'trigger'       // manual-trigger, webhook-trigger, schedule-trigger
  | 'ai'            // ai-agent node (data stores category as 'ai')
  | 'ai-agent'      // alias kept for backward compatibility
  | 'llm'           // openai-llm
  | 'ai-memory'     // memory store nodes
  | 'tool'          // tool nodes (agent functions)
  | 'logic'         // if-condition, switch, filter (category stored in node.data.category)
  | 'http'          // http-request, graphql-request, rest-api, soap-api, grpc-call
  | 'database'      // mysql-query, postgresql-query, sqlserver-query, mongodb-query, redis-query
  | 'transform'     // json-transform, xml-transform, csv-parse, merge, split, aggregate, loop
  | 'notification'  // send-email, slack-message, teams-message, discord-message, telegram-message, sms-send
  | 'file'          // read-file, write-file, delete-file
  | 'storage'       // aws-s3, s3-upload, s3-download, google-drive, dropbox, ftp-upload, ftp-download, azure-blob, gcs-upload
  | 'devops'        // ssh-execute, docker-run, kubernetes-deploy
  | 'payment'       // stripe-payment, paypal-payment
  | 'ai-service'    // openai-chat, anthropic-chat, huggingface-inference, cohere-generate, stabilityai-image, elevenlabs-tts, assemblyai-transcribe
  | 'vector-db'     // pinecone-upsert, pinecone-query, weaviate-insert, weaviate-search
  | 'dev-tools'     // github-create-issue, github-create-pr, gitlab-create-mr, jira-create-issue, linear-create-issue
  | 'productivity'  // notion-create-page, airtable-create-record, google-sheets-append, google-calendar-create, outlook-send-email
  | 'crm'           // salesforce-create-lead, hubspot-create-contact, zendesk-create-ticket, intercom-send-message
  | 'blockchain'    // ipfs-upload, ipfs-download, ethereum-transaction, smart-contract-call, nft-mint, web3-wallet
  | 'hil'           // human-in-loop: approval, hil-form, chat-receive
  | 'subflow'       // subflow (nested workflow)
  | 'connector'     // generic connector nodes
  | 'rag'           // flow-rag: knowledge insert, delete, update
  | 'communication' // email/messaging integrations (gmail, smtp, etc.)
  | 'default';      // fallback

export type TabName =
  | 'configuration'
  | 'credentials'
  | 'agent'
  | 'knowledgeBase'
  | 'channel'
  | 'llmModel'
  | 'memory'
  | 'agentFunction'
  | 'database'
  | 'transform'
  | 'notification'
  | 'storage'
  | 'devops'
  | 'payment'
  | 'aiService'
  | 'vectorDb'
  | 'devTools'
  | 'productivity'
  | 'crm'
  | 'blockchain';

/**
 * Rules interface defining allowed categories and tabs for a port type
 */
export interface PortSelectionRule {
  portType: PortType;
  description: string;
  allowedNodeCategories: NodeCategory[];
  visibleTabs: TabName[];
  hideTabCategories: NodeCategory[]; // Categories whose tabs should be hidden
  excludeTriggerNodes: boolean; // If true, exclude trigger node categories
}

/**
 * All non-trigger processing node categories — used for standard output/input rules.
 * When a new node category is added, add it here to automatically include it in
 * the standard output → input connectivity.
 */
const ALL_PROCESSING_CATEGORIES: NodeCategory[] = [
  'ai',       // actual category value in node.data.category for ai-agent nodes
  'ai-agent', // alias
  'llm',
  'ai-memory',
  'tool',
  'logic',
  'http',
  'database',
  'transform',
  'notification',
  'file',
  'storage',
  'devops',
  'payment',
  'ai-service',
  'vector-db',
  'dev-tools',
  'productivity',
  'crm',
  'blockchain',
  'hil',
  'subflow',
  'connector',
  'rag',
  'communication',
  'default',
];

/**
 * All tabs shown for general processing nodes.
 */
const ALL_PROCESSING_TABS: TabName[] = [
  'configuration',
  'credentials',
  'agent',
  'knowledgeBase',
  'channel',
  'llmModel',
  'memory',
  'agentFunction',
  'database',
  'transform',
  'notification',
  'storage',
  'devops',
  'payment',
  'aiService',
  'vectorDb',
  'devTools',
  'productivity',
  'crm',
  'blockchain',
];

/**
 * Port-based node selection rules
 * Define which node types can be connected to each port type.
 *
 * Node handle → allowed target categories:
 *
 *  trigger output      → all processing nodes (no triggers)
 *  standard output     → all processing nodes (no triggers)
 *  standard input      → receives from all processing nodes (no triggers)
 *  ai-agent llm        → llm only (openai-llm)
 *  ai-agent memory     → ai-memory only
 *  ai-agent tool       → tool only (multi)
 *  ai-agent extends      → ai-agent only (extends source → base-node target)
 *  ai-agent team-leader  → ai-agent only (team-leader source → team-member target, multi)
 *  agentInheritTarget  → receives from agentInherit (source on other agent)
 *  agentParticipantTarget → receives from agentParticipation (source on other agent, multi)
 *  conditionTrue/False/Case → all processing nodes (no triggers)
 *  knowledgeBase       → ai-agent, tool, llm, connector
 */
export const PORT_SELECTION_RULES: Record<PortType, PortSelectionRule> = {
  // ─── Standard output port ───────────────────────────────────────────────
  // CustomNode / SubFlowNode right handle: connects to any non-trigger node.
  output: {
    portType: 'output',
    description: 'Standard output port — connects to any non-trigger processing node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: [],
    excludeTriggerNodes: true,
  },

  // ─── Trigger node output ─────────────────────────────────────────────────
  // CircleNode (manual-trigger, webhook-trigger, schedule-trigger) output.
  // Cannot chain to another trigger.
  trigger: {
    portType: 'trigger',
    description: 'Trigger node output — connects to any processing node, no further triggers',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: ['trigger'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent bottom: LLM handle ─────────────────────────────────────────
  // Handle id: "llm" (source) → connects only to openai-llm (target)
  llm: {
    portType: 'llm',
    description: 'AI Agent LLM port (bottom) — only connects to LLM nodes (e.g. openai-llm)',
    allowedNodeCategories: ['llm'],
    visibleTabs: ['configuration', 'credentials', 'llmModel'],
    hideTabCategories: ['trigger', 'ai-agent', 'ai-memory', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent bottom: Memory handle ──────────────────────────────────────
  // Handle id: "memory" (source) → connects only to ai-memory nodes
  memory: {
    portType: 'memory',
    description: 'AI Agent Memory port (bottom) — only connects to memory store nodes',
    allowedNodeCategories: ['ai-memory'],
    visibleTabs: ['configuration', 'memory'],
    hideTabCategories: ['trigger', 'ai-agent', 'llm', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent bottom: Tool handle ────────────────────────────────────────
  // Handle id: "tool" (source, multi) → connects only to tool nodes
  tool: {
    portType: 'tool',
    description: 'AI Agent Tool port (bottom, multi) — only connects to tool nodes',
    allowedNodeCategories: ['tool'],
    visibleTabs: ['configuration', 'credentials', 'agentFunction', 'knowledgeBase'],
    hideTabCategories: ['trigger', 'ai-agent', 'llm', 'ai-memory', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent right: Inherit source handle ───────────────────────────────
  // Handle id: "extends" (source) → connects to another ai-agent's "base-node" target
  agentInherit: {
    portType: 'agentInherit',
    description: 'AI Agent extends source (left) — connects to another AI Agent base-node input',
    allowedNodeCategories: ['ai', 'ai-agent'],
    visibleTabs: ['configuration', 'credentials', 'agent'],
    hideTabCategories: ['trigger', 'llm', 'ai-memory', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent right: Participation source handle ─────────────────────────
  // Handle id: "team-leader" (source, multi) → connects to another ai-agent's "team-member" target
  agentParticipation: {
    portType: 'agentParticipation',
    description: 'AI Agent team-leader source (top, multi) — connects to another AI Agent team-member input',
    allowedNodeCategories: ['ai', 'ai-agent'],
    visibleTabs: ['configuration', 'credentials', 'agent'],
    hideTabCategories: ['trigger', 'llm', 'ai-memory', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent top: InheritFrom target handle ─────────────────────────────
  // Handle id: "base-node" (target) — receives connection from another agent's "extends" source.
  // When user clicks this port to search for a source, only show ai-agent nodes.
  agentInheritTarget: {
    portType: 'agentInheritTarget',
    description: 'AI Agent base-node input (bottom) — receives from another AI Agent extends output',
    allowedNodeCategories: ['ai', 'ai-agent'],
    visibleTabs: ['configuration', 'credentials', 'agent'],
    hideTabCategories: ['trigger', 'llm', 'ai-memory', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── AI Agent top: Participant target handle ──────────────────────────────
  // Handle id: "team-member" (target, multi) — receives connection from another agent's "team-leader" source.
  agentParticipantTarget: {
    portType: 'agentParticipantTarget',
    description: 'AI Agent team-member input (bottom, multi) — receives from another AI Agent team-leader output',
    allowedNodeCategories: ['ai', 'ai-agent'],
    visibleTabs: ['configuration', 'credentials', 'agent'],
    hideTabCategories: ['trigger', 'llm', 'ai-memory', 'tool', 'logic', 'http',
      'database', 'transform', 'notification', 'file', 'storage', 'devops',
      'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── Standard input port ─────────────────────────────────────────────────
  // Left handle on CustomNode, LlmNode, SubFlowNode — receives from any output.
  input: {
    portType: 'input',
    description: 'Standard input port — receives from any non-trigger processing node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: [],
    excludeTriggerNodes: true,
  },

  // ─── Knowledge Base port ─────────────────────────────────────────────────
  knowledgeBase: {
    portType: 'knowledgeBase',
    description: 'Knowledge base port — connects to ai-agent, tool, llm, or connector nodes',
    allowedNodeCategories: ['ai', 'ai-agent', 'tool', 'llm', 'connector'],
    visibleTabs: ['configuration', 'credentials', 'knowledgeBase', 'agent'],
    hideTabCategories: ['trigger', 'ai-memory', 'logic', 'http', 'database', 'transform',
      'notification', 'file', 'storage', 'devops', 'payment', 'ai-service',
      'vector-db', 'dev-tools', 'productivity', 'crm', 'blockchain', 'subflow'],
    excludeTriggerNodes: true,
  },

  // ─── Condition node TRUE branch output ───────────────────────────────────
  // DiamondNode if-condition TRUE output handle
  conditionTrue: {
    portType: 'conditionTrue',
    description: 'Condition TRUE branch — connects to any non-trigger processing node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: ['trigger'],
    excludeTriggerNodes: true,
  },

  // ─── Condition node FALSE branch output ──────────────────────────────────
  // DiamondNode if-condition FALSE output handle
  conditionFalse: {
    portType: 'conditionFalse',
    description: 'Condition FALSE branch — connects to any non-trigger processing node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: ['trigger'],
    excludeTriggerNodes: true,
  },

  // ─── Switch / Filter case branch output ──────────────────────────────────
  // DiamondNode switch-case / filter match/no-match output handles
  conditionCase: {
    portType: 'conditionCase',
    description: 'Condition case/match branch — connects to any non-trigger processing node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: ['trigger'],
    excludeTriggerNodes: true,
  },

  // ─── Component port (my-components → component) ───────────────────────────
  // Any node can expose its configuration as a "component" satellite.
  // The "+" button on the my-components handle opens this picker.
  component: {
    portType: 'component',
    description: 'Component port — attach any node as a component of this node',
    allowedNodeCategories: ALL_PROCESSING_CATEGORIES,
    visibleTabs: ALL_PROCESSING_TABS,
    hideTabCategories: [],
    excludeTriggerNodes: false,
  },
};

/**
 * Get selection rule for a specific port type
 * @param portType - The type of port being clicked
 * @returns PortSelectionRule for the port type
 */
export const getPortSelectionRule = (portType: PortType): PortSelectionRule => {
  const rule = PORT_SELECTION_RULES[portType];
  if (!rule) {
    console.warn(`[portNodeSelectionRules] Unknown port type: ${portType}, using default 'output' rule`);
    return PORT_SELECTION_RULES.output;
  }
  return rule;
};

/**
 * Validation interface for port-to-node connections
 */
export interface ConnectionValidationResult {
  isValid: boolean;
  reason?: string;
  allowedCategories?: NodeCategory[];
}

// All frontend-known NodeCategory values — DB may send PascalCase strings (e.g. 'Infrastructure')
// that aren't in this set; those are normalized to 'default' so connections still work.
const ALL_KNOWN_CATEGORIES = new Set<string>([
  'trigger', 'ai', 'ai-agent', 'llm', 'ai-memory', 'tool', 'logic', 'http',
  'database', 'transform', 'notification', 'file', 'storage', 'devops',
  'payment', 'ai-service', 'vector-db', 'dev-tools', 'productivity', 'crm',
  'blockchain', 'hil', 'subflow', 'connector', 'rag', 'default',
]);

/**
 * Validate if a node can be connected to a specific port
 * @param nodeCategory - The category of the node being connected (may be a raw DB string)
 * @param portType - The type of port being connected to
 * @returns ValidationResult with validation status and reason
 */
export const validateNodeForPort = (
  nodeCategory: NodeCategory,
  portType: PortType
): ConnectionValidationResult => {
  const rule = getPortSelectionRule(portType);

  // Normalize unknown DB category strings (e.g. 'Infrastructure', 'DevOps') to 'default'
  const effectiveCategory: NodeCategory = ALL_KNOWN_CATEGORIES.has(nodeCategory)
    ? nodeCategory
    : 'default';

  // Check if node category is allowed
  if (!rule.allowedNodeCategories.includes(effectiveCategory)) {
    return {
      isValid: false,
      reason: `Node category '${nodeCategory}' is not allowed for port type '${portType}'. ${rule.description}`,
      allowedCategories: rule.allowedNodeCategories,
    };
  }

  // Check if trigger nodes should be excluded
  if (rule.excludeTriggerNodes && effectiveCategory === 'trigger') {
    return {
      isValid: false,
      reason: `Trigger nodes cannot be connected to '${portType}' ports. ${rule.description}`,
      allowedCategories: rule.allowedNodeCategories,
    };
  }

  return { isValid: true };
};

/**
 * Get allowed categories for a specific port type
 * Useful for filtering node search results
 * @param portType - The type of port being clicked
 * @returns Array of allowed NodeCategory values
 */
export const getAllowedCategoriesForPort = (portType: PortType): NodeCategory[] => {
  return getPortSelectionRule(portType).allowedNodeCategories;
};

/**
 * Get visible tabs for a specific port type
 * Used in node selection modal to show relevant configuration tabs
 * @param portType - The type of port being clicked
 * @returns Array of visible TabName values
 */
export const getVisibleTabsForPort = (portType: PortType): TabName[] => {
  return getPortSelectionRule(portType).visibleTabs;
};

/**
 * Check if trigger node categories should be hidden for a port type
 * @param portType - The type of port being clicked
 * @returns boolean - true if trigger nodes should be excluded
 */
export const shouldExcludeTriggerNodesForPort = (portType: PortType): boolean => {
  return getPortSelectionRule(portType).excludeTriggerNodes;
};

/**
 * Check if a specific tab should be visible for a port type
 * @param tabName - The tab to check
 * @param portType - The type of port being clicked
 * @returns boolean - true if tab should be visible
 */
export const isTabVisibleForPort = (tabName: TabName, portType: PortType): boolean => {
  const visibleTabs = getVisibleTabsForPort(portType);
  return visibleTabs.includes(tabName);
};

/**
 * Get node categories that should be hidden for a port type
 * @param portType - The type of port being clicked
 * @returns Array of NodeCategory values to hide
 */
export const getHiddenTabCategoriesForPort = (portType: PortType): NodeCategory[] => {
  return getPortSelectionRule(portType).hideTabCategories;
};

/**
 * Filter nodes based on port type rules
 * Returns filtered node array based on allowed categories
 * @param nodes - Array of nodes to filter
 * @param portType - The type of port being clicked
 * @returns Filtered array of nodes that can connect to the port
 */
export const filterNodesByPortType = <T extends { category: NodeCategory }>(
  nodes: T[],
  portType: PortType
): T[] => {
  const allowedCategories = getAllowedCategoriesForPort(portType);
  return nodes.filter(node => allowedCategories.includes(node.category));
};

/**
 * Get human-readable description for a port type's selection rules
 * Useful for UI tooltips and help text
 * @param portType - The type of port
 * @returns Description string
 */
export const getPortTypeDescription = (portType: PortType): string => {
  return getPortSelectionRule(portType).description;
};

/**
 * Check if a source port type can connect to a target node
 * Complete validation including reverse checks
 * @param sourcePortType - The type of port being clicked
 * @param targetNodeCategory - The category of the node being connected
 * @returns CompleteValidationResult
 */
export const validateConnectionComplete = (
  sourcePortType: PortType,
  targetNodeCategory: NodeCategory
): ConnectionValidationResult => {
  return validateNodeForPort(targetNodeCategory, sourcePortType);
};

/**
 * Get allowed node categories as a formatted string
 * Useful for error messages
 * @param portType - The type of port
 * @returns Formatted string of allowed categories
 */
export const getFormattedAllowedCategories = (portType: PortType): string => {
  const categories = getAllowedCategoriesForPort(portType);
  if (categories.length === 0) return 'None';
  if (categories.length === 1) return categories[0];
  return categories.slice(0, -1).join(', ') + ' or ' + categories[categories.length - 1];
};
